import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, CircularProgress, Typography } from '@mui/material';

import AgencyCorporationSupportSetter from '../../../../components/AgencyCorporationSupportSetter';
import FreeTextSearchFilter, {
  SEARCHBAR_BACKGROUND,
} from '../../../../components/FreeTextSearchFilter';
import {
  selectCorporationsAgencySupport,
  selectCorporationsAgencySupportCount,
  selectIsCorporationsAgencySupportLoading,
} from '../../../../store/selectors/dashboardSelector';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import { getCorporationsAgencySupport } from '../../dashboardReducer';

const AgencyCorporationEditPage = () => {
  const { id } = useParams();

  const [filterAndSortData, setFilterAndSortData] = React.useState({
    agencyId: id,
    page: 0,
    limit: 5,
  });

  const [searchText, setSearchText] = React.useState('');
  const dispatch = useDispatch();
  const sentinelRef = React.useRef(null);
  const corporationsAgencySupportLoading = useSelector(selectIsCorporationsAgencySupportLoading);
  const corporationsAgencySupport = useSelector(selectCorporationsAgencySupport);
  const corporationsAgencySupportCount = useSelector(selectCorporationsAgencySupportCount);

  useEffect(() => {
    dispatch(getCorporationsAgencySupport(filterAndSortData));
  }, [filterAndSortData, dispatch]);

  const handleIntersection = useCallback(
    (entries) => {
      if (
        entries?.[0].isIntersecting &&
        corporationsAgencySupportCount > corporationsAgencySupport?.length &&
        !corporationsAgencySupportLoading
      ) {
        setFilterAndSortData((prevVal) => ({
          ...prevVal,
          page: prevVal.page + 1,
        }));
      }
    },
    [corporationsAgencySupportCount, corporationsAgencySupport, corporationsAgencySupportLoading]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [handleIntersection]);

  const filteredCorporations = React.useMemo(() => {
    if (!searchText.trim()) {
      return corporationsAgencySupport;
    }

    const res = corporationsAgencySupport.filter(({ corporation }) =>
      corporation?.name?.toLowerCase()?.includes(searchText?.toLowerCase())
    );
    return res;
  }, [corporationsAgencySupport, searchText]);

  const handleOnSearchChange = useCallback((searchTerm) => {
    setSearchText(searchTerm[0].value || '');
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        pt: '24px',
        gap: '18px',
      }}
    >
      <Typography sx={{ fontSize: '20px', fontFamily: PRIMARY_FONT[700] }}>
        {LanguageConverterV2('dashboard.agency.edit.addOrEditCorporations')}
      </Typography>
      <FreeTextSearchFilter
        placeholder={LanguageConverterV2('dashboard.agency.edit.searchCorporations')}
        onValueChange={handleOnSearchChange}
        background={SEARCHBAR_BACKGROUND.DEFAULT}
      />
      <div>
        {filteredCorporations?.map((corporationAgencySupport) => (
          <AgencyCorporationSupportSetter
            key={corporationAgencySupport?._id}
            currentlySetLocationIds={corporationAgencySupport?.locations}
            currentlySetPositionTemplateIds={corporationAgencySupport?.positionTemplates}
            corpName={corporationAgencySupport?.corporation?.name}
            corpId={corporationAgencySupport?.corporation?.ID}
            agencyId={id}
          />
        ))}
      </div>
      <div ref={sentinelRef} />
      {corporationsAgencySupportLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default AgencyCorporationEditPage;
